import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAdmin } from '../../context/AdminContext';
import adminApiClient from '../../api/adminApiClient';
import config from '../../utils/config';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const { currentAdmin } = useAdmin();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState({
    users: { total: 0, free: 0, basic: 0, premium: 0 },
    projects: { total: 0 },
    payments: { totalMonthly: 0, lastMonth: 0, growthRate: 0 }
  });

  // Fetch dashboard statistics using the adminApiClient
  const fetchDashboardStats = async () => {
    try {
      console.log('Fetching dashboard stats...');
      setIsLoading(true);
      
      // Check for admin token
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) {
        console.error('No admin token found in localStorage!');
        return;
      }
      
      // Use the adminApiClient to make the request to the correct backend URL
      console.log('Using endpoint:', config.ADMIN_ENDPOINTS.DASHBOARD_STATS || '/admin/dashboard/stats');
      const response = await adminApiClient.get(
        config.ADMIN_ENDPOINTS.DASHBOARD_STATS || '/admin/dashboard/stats'
      );
      
      console.log('Dashboard API response:', response);
      
      if (response.status === 'success') {
        const data = response.data || {};
        
        // Create a safe copy with defaults for any missing properties
        const safeData = {
          users: {
            total: data.users?.total || 0,
            free: data.users?.free || 0,
            basic: data.users?.basic || 0,
            premium: data.users?.premium || 0
          },
          projects: {
            total: data.projects?.total || 0
          },
          payments: {
            totalMonthly: data.payments?.totalMonthly || 0,
            lastMonth: data.payments?.lastMonth || 0,
            growthRate: data.payments?.growthRate || 0
          }
        };
        
        console.log('Setting dashboard stats:', safeData);
        setDashboardStats(safeData);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error.response || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Debug function to check API endpoint manually
  const debugFetchStats = async () => {
    console.log('Manually checking API using adminApiClient...');
    
    try {
      // Use adminApiClient which should have the correct base URL and auth token setup
      const endpoint = config.ADMIN_ENDPOINTS.DASHBOARD_STATS || '/admin/dashboard/stats';
      console.log('Endpoint:', endpoint);
      
      const response = await adminApiClient.get(endpoint);
      console.log('Manual API check result:', response);
    } catch (error) {
      console.error('Manual API check failed:', error.response || error.message);
    }
  };

  // Use useEffect to fetch data once
  useEffect(() => {
    console.log('AdminDashboard mounted');
    console.log('Config endpoints:', config.ADMIN_ENDPOINTS);
    
    // Fetch stats on mount
    fetchDashboardStats();
    
    // Also try a manual debug fetch
    setTimeout(debugFetchStats, 2000);
    
    // Set up interval to refresh stats every 5 minutes
    const interval = setInterval(() => {
      fetchDashboardStats();
    }, 5 * 60 * 1000);
    
    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, []); // Empty dependency array to run only once

  // Get stats with safe defaults to prevent runtime errors
  const users = dashboardStats?.users || { total: 0, free: 0, basic: 0, premium: 0 };
  const projects = dashboardStats?.projects || { total: 0 };
  const payments = dashboardStats?.payments || { totalMonthly: 0, lastMonth: 0, growthRate: 0 };

  // Main statistics cards
  const stats = [
    { 
      name: 'Total Users', 
      value: isLoading ? '...' : users.total.toString(), 
      icon: '👥', 
      color: 'bg-indigo-500' 
    },
    { 
      name: 'Total Projects', 
      value: isLoading ? '...' : projects.total.toString(), 
      icon: '📂', 
      color: 'bg-green-500' 
    },
    { 
      name: 'Total Monthly Payments', 
      value: isLoading ? '...' : `$${payments.totalMonthly.toLocaleString()}`, 
      icon: '💰', 
      color: 'bg-yellow-500' 
    }
  ];
  
  // User breakdown stats
  const userBreakdown = [
    { 
      name: 'Free Users', 
      value: isLoading ? '...' : users.free.toString(), 
      icon: '🆓', 
      color: 'bg-blue-400' 
    },
    { 
      name: 'Basic Users', 
      value: isLoading ? '...' : users.basic.toString(), 
      icon: '🔹', 
      color: 'bg-blue-500' 
    },
    { 
      name: 'Premium Users', 
      value: isLoading ? '...' : users.premium.toString(), 
      icon: '⭐', 
      color: 'bg-blue-600' 
    }
  ];
  
  const actionCards = [
    { 
      title: 'User Management',
      description: 'Manage user accounts, subscriptions, and permissions.',
      icon: '👥',
      link: '/admin/users',
      color: 'blue'
    },
    { 
      title: 'Admin Management',
      description: 'Manage admin accounts and access controls.',
      icon: '🔑',
      link: '/admin/admins',
      color: 'purple'
    },
    { 
      title: 'System Settings',
      description: 'Configure application settings, API keys, and defaults.',
      icon: '⚙️',
      link: '/admin/settings',
      color: 'green'
    }
  ];

  // Navigate to a page
  const navigateTo = (path) => {
    navigate(path);
  };

  // Calculate growth rate for display
  const formatGrowthRate = (rate) => {
    const prefix = rate >= 0 ? '+' : '';
    return `${prefix}${rate.toFixed(1)}%`;
  };

  // For displaying payment growth rate
  const growthRateColor = payments.growthRate >= 0 
    ? 'text-green-600' 
    : 'text-red-600';

  return (
    <AdminLayout title="Admin Dashboard">
      <div className="dashboard-container">
        <div className="welcome-section">
          <h2 className="section-title">Welcome back, {currentAdmin?.name || 'Admin'}</h2>
          <p className="section-description">Here's what's happening with your application today.</p>
        </div>

        {/* Main Stats Overview */}
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className={`stat-icon ${stat.color}`}>
                <span>{stat.icon}</span>
              </div>
              <div className="stat-details">
                <h3 className="stat-value">{stat.value}</h3>
                <p className="stat-name">{stat.name}</p>
                {stat.name === 'Total Monthly Payments' && !isLoading && (
                  <p className={`text-sm mt-1 ${growthRateColor}`}>
                    {formatGrowthRate(payments.growthRate)} from last month
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* User Breakdown Section */}
        <h2 className="section-title mt-8">User Breakdown</h2>
        <div className="stats-grid">
          {userBreakdown.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className={`stat-icon ${stat.color}`}>
                <span>{stat.icon}</span>
              </div>
              <div className="stat-details">
                <h3 className="stat-value">{stat.value}</h3>
                <p className="stat-name">{stat.name}</p>
                {!isLoading && (
                  <p className="text-sm mt-1 text-gray-500">
                    {users.total > 0 
                      ? `${Math.round((users[stat.name.toLowerCase().split(' ')[0]] / users.total) * 100)}%` 
                      : '0%'}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <h2 className="section-title mt-8">Quick Actions</h2>
        <div className="action-cards">
          {actionCards.map((card, index) => (
            <div 
              key={index} 
              className={`action-card ${card.color} cursor-pointer hover:shadow-lg transition-shadow`}
              onClick={() => navigateTo(card.link)}
            >
              <div className="action-header">
                <span className="action-icon">{card.icon}</span>
                <h3 className="action-title">{card.title}</h3>
              </div>
              <p className="action-description">{card.description}</p>
              <div className="flex justify-end mt-4">
                <button 
                  className={`action-button ${card.color}`}
                >
                  Manage
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;